// VisitPlanningPage.tsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Download, Users, Building, Search, X, Eye, Clock, CalendarDays, Calendar, MapPin, Stethoscope, UserRound, BadgeCheck, Navigation, ChevronLeft, ChevronRight } from "lucide-react";

import PageLayout from "../components/PageLayout";
import { VisitPlanningPageProps } from "../components/common/types";
import { FirmVisits, Visits } from "../types/visit";
import { getAllDoctorVisits, getAllFirmVisits } from "../api/visit";
import { useAuth } from "../context/AuthContext";
import { fmtDate, fmtTime } from "../utils/datetime";

const exportToCsv = (filename: string, data: any[], type: "doctor" | "firm"): void => {
  let headers: string[];
  let rows: any[];

  if (type === "doctor") {
    headers = ["Doctor Name", "Code", "Specialization", "City", "Date", "Time", "Status", "Details"];
    rows = data.map((visit: Visits) => [
      visit.doctor_name,
      visit.doctor_code,
      visit.specialization,
      visit.doctor_city,
      visit.tour_plan_date,
      `${fmtTime(visit.from)} - ${visit.to ? fmtTime(visit.to) : "Ongoing"}`,
      visit.status,
      visit.details || "",
    ]);
  } else {
    headers = ["Firm Name", "Type", "City", "Date", "Time", "Status", "Details"];
    rows = data.map((visit: FirmVisits) => [
      visit.firm_name,
      visit.firm_type,
      visit.firm_city,
      visit.tour_plan_date,
      `${fmtTime(visit.from)} - ${visit.to ? fmtTime(visit.to) : "Ongoing"}`,
      visit.status,
      visit.details || "",
    ]);
  }

  const csvContent = [
    headers.join(","),
    ...rows.map((row) =>
      row.map((field: any) => `"${field?.toString().replace(/"/g, '""') || ""}"`).join(",")
    ),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
};

type MainTab = "doctors" | "firms";
type SubTab = "all" | "open" | "closed" | "today" | "skipped";

const getStatusBadge = (status: string, skip?: boolean) => {
  if (skip) return "bg-yellow-100 text-yellow-800";
  switch (status) {
    case "open":
      return "bg-green-100 text-green-800";
    case "closed":
      return "bg-red-100 text-red-800";
    case "planned":
      return "bg-blue-100 text-blue-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

// Format a Date using its LOCAL calendar fields (not toISOString, which
// converts to UTC and can shift the date by a day depending on the
// viewer's timezone offset — e.g. IST users near midnight).
const toLocalISODate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const getTodayLocal = () => toLocalISODate(new Date());

const VisitPlanningPage: React.FC<VisitPlanningPageProps> = ({ onLogout, onNavigate }) => {
  const [visits, setVisits] = useState<Visits[]>([]);
  const [firmVisits, setFirmVisits] = useState<FirmVisits[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [activeMainTab, setActiveMainTab] = useState<MainTab>("doctors");
  const [activeDoctorSubTab, setActiveDoctorSubTab] = useState<SubTab>("all");
  const [activeFirmSubTab, setActiveFirmSubTab] = useState<SubTab>("all");

  const [searchQuery, setSearchQuery] = useState("");
  const [showSearch, setShowSearch] = useState(false);

  // Report filters: Employee / Doctor / Firm / Location / Date range.
  // "" means "All" for every select below.
  const [filterEmployeeId, setFilterEmployeeId] = useState<string>("");
  const [filterDoctorId, setFilterDoctorId] = useState<string>("");
  const [filterFirmId, setFilterFirmId] = useState<string>("");
  const [filterLocationId, setFilterLocationId] = useState<string>("");
  const [filterFromDate, setFilterFromDate] = useState<string>("");
  const [filterToDate, setFilterToDate] = useState<string>("");

  const PER_PAGE = 10;
  const [doctorPage, setDoctorPage] = useState(1);
  const [firmPage, setFirmPage] = useState(1);

  const { user } = useAuth();

  const loadData = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const [visitsData, firmVisitsData] = await Promise.all([
        getAllDoctorVisits(user.tenant_id),
        getAllFirmVisits(user.tenant_id),
      ]);
      setVisits(visitsData);
      setFirmVisits(firmVisitsData);
    } catch (error) {
      console.error("Error loading data:", error);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    if (user?.tenant_id) loadData();
  }, [loadData, user]);

  const toggleSearch = () => {
    setShowSearch((p) => !p);
    if (!showSearch) {
      setTimeout(() => document.getElementById("search-input")?.focus(), 100);
    } else {
      setSearchQuery("");
    }
  };

  // Filter dropdown options, derived from whatever visit data is already
  // loaded (no extra API calls) so the lists always match what's filterable.
  const employeeOptions = useMemo(() => {
    const map = new Map<number, string>();
    [...visits, ...firmVisits].forEach((v) => {
      if (v.employee_id != null && !map.has(v.employee_id)) {
        map.set(v.employee_id, v.employee_code ? `${v.employee_name || "-"} (${v.employee_code})` : (v.employee_name || "-"));
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visits, firmVisits]);

  const doctorOptions = useMemo(() => {
    const map = new Map<number, string>();
    visits.forEach((v) => {
      if (v.doctor_id != null && !map.has(v.doctor_id)) {
        map.set(v.doctor_id, v.doctor_code ? `${v.doctor_name || "-"} (${v.doctor_code})` : (v.doctor_name || "-"));
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visits]);

  const firmOptions = useMemo(() => {
    const map = new Map<number, string>();
    firmVisits.forEach((v) => {
      if (v.firm_id != null && !map.has(v.firm_id)) {
        map.set(v.firm_id, v.firm_name || "-");
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [firmVisits]);

  const locationOptions = useMemo(() => {
    const map = new Map<number, string>();
    visits.forEach((v) => {
      if (v.doctor_location_id != null && !map.has(v.doctor_location_id)) {
        map.set(v.doctor_location_id, v.doctor_location || v.doctor_city || "-");
      }
    });
    firmVisits.forEach((v) => {
      if (v.firm_location_id != null && !map.has(v.firm_location_id)) {
        map.set(v.firm_location_id, v.firm_location || v.firm_city || "-");
      }
    });
    return Array.from(map.entries())
      .map(([id, label]) => ({ id, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [visits, firmVisits]);

  const hasActiveFilters =
    !!filterEmployeeId || !!filterDoctorId || !!filterFirmId || !!filterLocationId || !!filterFromDate || !!filterToDate;

  const clearFilters = () => {
    setFilterEmployeeId("");
    setFilterDoctorId("");
    setFilterFirmId("");
    setFilterLocationId("");
    setFilterFromDate("");
    setFilterToDate("");
  };

  const getSubTabLabel = (tab: SubTab): string => {
    const labels: Record<SubTab, string> = {
      all: "All",
      open: "Open",
      closed: "Closed",
      today: "Today's",
      skipped: "Skipped",
    };
    return labels[tab];
  };

  const matchesDateRange = (date: string | undefined): boolean => {
    if (!filterFromDate && !filterToDate) return true;
    if (!date) return false;
    if (filterFromDate && date < filterFromDate) return false;
    if (filterToDate && date > filterToDate) return false;
    return true;
  };

  // Filter + sort doctor visits (latest first)
  const filteredDoctors = visits
    .filter((visit) => {
      const matchesTab = (() => {
        switch (activeDoctorSubTab) {
          case "open":    return visit.status === "open";
          case "closed":  return visit.status === "closed" && !visit.skip;
          case "today":   return visit.tour_plan_date === getTodayLocal();
          case "skipped": return !!visit.skip;
          default:        return true;
        }
      })();

      const matchesFilter = matchesDateRange(visit.tour_plan_date);
      const matchesEmployee = !filterEmployeeId || String(visit.employee_id) === filterEmployeeId;
      const matchesDoctor = !filterDoctorId || String(visit.doctor_id) === filterDoctorId;
      const matchesLocation = !filterLocationId || String(visit.doctor_location_id ?? "") === filterLocationId;

      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        q === "" ||
        visit.doctor_name?.toLowerCase().includes(q) ||
        visit.specialization?.toLowerCase().includes(q) ||
        visit.doctor_city?.toLowerCase().includes(q) ||
        visit.doctor_code?.toLowerCase().includes(q) ||
        visit.employee_name?.toLowerCase().includes(q) ||
        visit.employee_code?.toLowerCase().includes(q);

      return matchesTab && matchesFilter && matchesEmployee && matchesDoctor && matchesLocation && matchesSearch;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Filter + sort firm visits (latest first)
  const filteredFirms = firmVisits
    .filter((visit) => {
      const matchesTab = (() => {
        switch (activeFirmSubTab) {
          case "open":    return visit.status === "open";
          case "closed":  return visit.status === "closed" && !visit.skip;
          case "today":   return visit.tour_plan_date === getTodayLocal();
          case "skipped": return !!visit.skip;
          default:        return true;
        }
      })();

      const matchesFilter = matchesDateRange(visit.tour_plan_date);
      const matchesEmployee = !filterEmployeeId || String(visit.employee_id) === filterEmployeeId;
      const matchesFirm = !filterFirmId || String(visit.firm_id) === filterFirmId;
      const matchesLocation = !filterLocationId || String(visit.firm_location_id ?? "") === filterLocationId;

      const q = searchQuery.trim().toLowerCase();
      const matchesSearch =
        q === "" ||
        visit.firm_name?.toLowerCase().includes(q) ||
        visit.firm_type?.toLowerCase().includes(q) ||
        visit.firm_city?.toLowerCase().includes(q) ||
        visit.employee_name?.toLowerCase().includes(q) ||
        visit.employee_code?.toLowerCase().includes(q);

      return matchesTab && matchesFilter && matchesEmployee && matchesFirm && matchesLocation && matchesSearch;
    })
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

  // Reset pagination whenever the active tab's filters/search change so
  // stale page numbers don't hide results.
  useEffect(() => {
    setDoctorPage(1);
  }, [activeDoctorSubTab, searchQuery, filterEmployeeId, filterDoctorId, filterLocationId, filterFromDate, filterToDate]);

  useEffect(() => {
    setFirmPage(1);
  }, [activeFirmSubTab, searchQuery, filterEmployeeId, filterFirmId, filterLocationId, filterFromDate, filterToDate]);

  const doctorTotalPages = Math.max(1, Math.ceil(filteredDoctors.length / PER_PAGE));
  const firmTotalPages = Math.max(1, Math.ceil(filteredFirms.length / PER_PAGE));

  const safeDoctorPage = Math.min(doctorPage, doctorTotalPages);
  const safeFirmPage = Math.min(firmPage, firmTotalPages);

  const paginatedDoctors = filteredDoctors.slice((safeDoctorPage - 1) * PER_PAGE, safeDoctorPage * PER_PAGE);
  const paginatedFirms = filteredFirms.slice((safeFirmPage - 1) * PER_PAGE, safeFirmPage * PER_PAGE);

  const renderPageNumbers = (totalPages: number, currentPage: number, onChange: (p: number) => void) => (
    <div className="flex items-center gap-1">
      <button
        onClick={() => onChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>

      {Array.from({ length: totalPages }, (_, i) => i + 1)
        .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
        .map((p, idx, arr) => (
          <React.Fragment key={p}>
            {idx > 0 && arr[idx - 1] !== p - 1 && (
              <span className="px-1 text-gray-400 text-sm">…</span>
            )}
            <button
              onClick={() => onChange(p)}
              className={`min-w-[32px] px-2 py-1.5 text-sm rounded-lg ${
                p === currentPage ? "bg-blue-600 text-white font-medium" : "border border-gray-300 hover:bg-gray-100"
              }`}
            >
              {p}
            </button>
          </React.Fragment>
        ))}

      <button
        onClick={() => onChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="p-1.5 border border-gray-300 rounded-lg hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );

  const handleExport = (): void => {
    if (activeMainTab === "doctors") exportToCsv("doctor_visits.csv", filteredDoctors, "doctor");
    else exportToCsv("firm_visits.csv", filteredFirms, "firm");
  };

  const hasResults = activeMainTab === "doctors" ? filteredDoctors.length > 0 : filteredFirms.length > 0;

  const [selectedDoctorVisit, setSelectedDoctorVisit] = useState<typeof visits[0] | null>(null);
  const [selectedFirmVisit, setSelectedFirmVisit] = useState<typeof firmVisits[0] | null>(null);

  return (
    <PageLayout onLogout={onLogout} currentPage="visit-planning" onNavigate={onNavigate}>
      <div className="p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Visit Planning</h1>
                <p className="text-blue-600 mt-1 text-sm md:text-base">Manage doctor and firm visits</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={toggleSearch}
                  className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {showSearch ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
                </button>
                <button
                  onClick={handleExport}
                  className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export
                </button>
              </div>
            </div>

            {/* Search */}
            {showSearch && (
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="search-input"
                    type="text"
                    className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder={`Search ${activeMainTab === "doctors"
                        ? "doctors, specializations, employees, cities..."
                        : "firms, types, employees, cities..."
                      }`}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    autoFocus
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Report Filters: Employee / Doctor|Firm / Location / Date Range */}
            <div className="mb-6 bg-white rounded-xl border border-gray-200 shadow-sm p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employee</label>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                      value={filterEmployeeId}
                      onChange={(e) => setFilterEmployeeId(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="">All Employees</option>
                      {employeeOptions.map((e) => (
                        <option key={e.id} value={e.id}>{e.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {activeMainTab === "doctors" ? (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Doctor</label>
                    <div className="relative">
                      <Stethoscope className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <select
                        value={filterDoctorId}
                        onChange={(e) => setFilterDoctorId(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                      >
                        <option value="">All Doctors</option>
                        {doctorOptions.map((d) => (
                          <option key={d.id} value={d.id}>{d.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Firm</label>
                    <div className="relative">
                      <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                      <select
                        value={filterFirmId}
                        onChange={(e) => setFilterFirmId(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                      >
                        <option value="">All Firms</option>
                        {firmOptions.map((f) => (
                          <option key={f.id} value={f.id}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Location</label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <select
                      value={filterLocationId}
                      onChange={(e) => setFilterLocationId(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none bg-white"
                    >
                      <option value="">All Locations</option>
                      {locationOptions.map((l) => (
                        <option key={l.id} value={l.id}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="date"
                      value={filterFromDate}
                      max={filterToDate || undefined}
                      onChange={(e) => setFilterFromDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="date"
                      value={filterToDate}
                      min={filterFromDate || undefined}
                      onChange={(e) => setFilterToDate(e.target.value)}
                      className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {hasActiveFilters && (
                <div className="mt-3 flex justify-end">
                  <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline">
                    Clear Filters
                  </button>
                </div>
              )}
            </div>

            {/* Main Tabs */}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setActiveMainTab("doctors")}
                  className={`flex-1 py-3 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeMainTab === "doctors" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <Users className="w-4 h-4" />
                  Doctors ({visits.length})
                </button>
                <button
                  onClick={() => setActiveMainTab("firms")}
                  className={`flex-1 py-3 px-4 text-sm font-medium rounded-md transition-colors flex items-center justify-center gap-2 ${activeMainTab === "firms" ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                    }`}
                >
                  <Building className="w-4 h-4" />
                  Firms ({firmVisits.length})
                </button>
              </div>
            </div>

            {/* Sub Tabs */}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
                {(["all", "open", "closed", "today", "skipped"] as SubTab[]).map((tab) => {
                  const isActive =
                    activeMainTab === "doctors" ? activeDoctorSubTab === tab : activeFirmSubTab === tab;

                  return (
                    <button
                      key={tab}
                      onClick={() => {
                        if (activeMainTab === "doctors") setActiveDoctorSubTab(tab);
                        else setActiveFirmSubTab(tab);
                      }}
                      className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${isActive ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                        }`}
                    >
                      {getSubTabLabel(tab)}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                {/* Table Header */}
                <div className="p-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {activeMainTab === "doctors"
                      ? `${getSubTabLabel(activeDoctorSubTab)} Doctor Visits`
                      : `${getSubTabLabel(activeFirmSubTab)} Firm Visits`}{" "}
                    (
                    {activeMainTab === "doctors"
                      ? `${filteredDoctors.length} / ${visits.length}`
                      : `${filteredFirms.length} / ${firmVisits.length}`}
                    )
                  </h2>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                  {activeMainTab === "doctors" ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Doctor
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Employee
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            City
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Specialization
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredDoctors.length === 0 ? (
                          <tr>
                            <td colSpan={10} className="px-6 py-10 text-center text-gray-500">
                              {searchQuery ? "No doctor visits match your search/filters." : "No doctor visits found."}
                            </td>
                          </tr>
                        ) : (
                          paginatedDoctors.map((visit) => (
                            <tr key={visit.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => setSelectedDoctorVisit(visit)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {visit.doctor_name || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.doctor_code || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="bg-yellow-200 px-2 py-1 rounded-md">
                                  {visit.employee_name || "-"} • {visit.employee_code || "-"}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.doctor_city || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.specialization || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.tour_plan_date ? fmtDate(visit.tour_plan_date) : "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {fmtTime(visit.from)} - {visit.to ? fmtTime(visit.to) : "Ongoing"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(visit.status, visit.skip)}`}>
                                  {visit.skip ? "Skipped" : visit.status?.charAt(0).toUpperCase() + visit.status?.slice(1)}
                                </span>
                              </td>

                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {visit.details || "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Firm
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Employee
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            City
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Time
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Details
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredFirms.length === 0 ? (
                          <tr>
                            <td colSpan={9} className="px-6 py-10 text-center text-gray-500">
                              {searchQuery ? "No firm visits match your search/filters." : "No firm visits found."}
                            </td>
                          </tr>
                        ) : (
                          paginatedFirms.map((visit) => (
                            <tr key={visit.id} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                <button
                                  onClick={() => setSelectedFirmVisit(visit)}
                                  className="text-blue-600 hover:text-blue-900 transition-colors"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                {visit.firm_name || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.firm_type || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                <span className="bg-green-200 px-2 py-1 rounded-md">
                                  {visit.employee_name || "-"} • {visit.employee_code || "-"}
                                </span>
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.firm_city || "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {visit.tour_plan_date ? fmtDate(visit.tour_plan_date) : "-"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                {fmtTime(visit.from)} - {visit.to ? fmtTime(visit.to) : "Ongoing"}
                              </td>

                              <td className="px-6 py-4 whitespace-nowrap text-sm">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(visit.status, visit.skip)}`}>
                                  {visit.skip ? "Skipped" : visit.status?.charAt(0).toUpperCase() + visit.status?.slice(1)}
                                </span>
                              </td>

                              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                                {visit.details || "-"}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  )}
                </div>

                {/* Footer */}
                <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                  <p className="text-sm text-gray-600">
                    Total {activeMainTab === "doctors" ? "Doctor Visits" : "Firm Visits"}:{" "}
                    <span className="font-medium">
                      {activeMainTab === "doctors" ? visits.length : firmVisits.length}
                    </span>
                    <span className="ml-2">
                      | Showing:{" "}
                      <span className="font-medium">
                        {activeMainTab === "doctors"
                          ? `${filteredDoctors.length === 0 ? 0 : (safeDoctorPage - 1) * PER_PAGE + 1}–${Math.min(safeDoctorPage * PER_PAGE, filteredDoctors.length)}`
                          : `${filteredFirms.length === 0 ? 0 : (safeFirmPage - 1) * PER_PAGE + 1}–${Math.min(safeFirmPage * PER_PAGE, filteredFirms.length)}`}
                      </span>
                      {" "}of{" "}
                      <span className="font-medium">
                        {activeMainTab === "doctors" ? filteredDoctors.length : filteredFirms.length}
                      </span>
                    </span>
                  </p>

                  {activeMainTab === "doctors"
                    ? doctorTotalPages > 1 && renderPageNumbers(doctorTotalPages, safeDoctorPage, setDoctorPage)
                    : firmTotalPages > 1 && renderPageNumbers(firmTotalPages, safeFirmPage, setFirmPage)}
                </div>
              </div>
            )}

            {/* Empty state cards (optional) */}
            {!isLoading && !hasResults && (
              <div className="mt-6 text-center py-12 bg-white rounded-lg border border-gray-200">
                {activeMainTab === "doctors" ? (
                  <>
                    <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No doctor visits found</h3>
                  </>
                ) : (
                  <>
                    <Building className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No firm visits found</h3>
                  </>
                )}
                <p className="text-gray-500">{searchQuery ? "Try a different search term" : "No visits match your filters"}</p>
              </div>
            )}
          </div>
        </div>
      {/* Doctor Visit Detail Modal */}
      {selectedDoctorVisit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Doctor Visit Details</h2>
              <button onClick={() => setSelectedDoctorVisit(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <UserRound className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Doctor</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedDoctorVisit.doctor_name || '-'}</p>
                  <p className="text-xs text-gray-500">{selectedDoctorVisit.doctor_code || ''}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Stethoscope className="w-5 h-5 text-indigo-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Specialization</p>
                  <p className="text-sm text-gray-900">{selectedDoctorVisit.specialization || '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Location</p>
                  <p className="text-sm text-gray-900">
                    {selectedDoctorVisit.doctor_location || selectedDoctorVisit.doctor_city || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BadgeCheck className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Employee</p>
                  <p className="text-sm text-gray-900">
                    {selectedDoctorVisit.employee_name || '-'}
                    {selectedDoctorVisit.employee_code && <span className="ml-1 text-gray-400 text-xs">({selectedDoctorVisit.employee_code})</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CalendarDays className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Date</p>
                  <p className="text-sm text-gray-900">{selectedDoctorVisit.tour_plan_date ? fmtDate(selectedDoctorVisit.tour_plan_date) : '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Time</p>
                  <p className="text-sm text-gray-900">
                    {fmtTime(selectedDoctorVisit.from)} – {selectedDoctorVisit.to ? fmtTime(selectedDoctorVisit.to) : 'Ongoing'}
                  </p>
                </div>
              </div>
              {selectedDoctorVisit.route_name && (
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Route</p>
                    <p className="text-sm text-gray-900">{selectedDoctorVisit.route_name}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Status</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(selectedDoctorVisit.status, selectedDoctorVisit.skip)}`}>
                  {selectedDoctorVisit.skip ? "Skipped" : selectedDoctorVisit.status?.charAt(0).toUpperCase() + selectedDoctorVisit.status?.slice(1)}
                </span>
              </div>
              {selectedDoctorVisit.details && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Details</p>
                  <p className="text-sm text-gray-700">{selectedDoctorVisit.details}</p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedDoctorVisit(null)}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Firm Visit Detail Modal */}
      {selectedFirmVisit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Firm Visit Details</h2>
              <button onClick={() => setSelectedFirmVisit(null)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-start gap-3">
                <Building className="w-5 h-5 text-blue-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Firm</p>
                  <p className="text-sm font-semibold text-gray-900">{selectedFirmVisit.firm_name || '-'}</p>
                  <p className="text-xs text-gray-500 capitalize">{selectedFirmVisit.firm_type || ''}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Location</p>
                  <p className="text-sm text-gray-900">
                    {selectedFirmVisit.firm_location || selectedFirmVisit.firm_city || '-'}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <BadgeCheck className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Employee</p>
                  <p className="text-sm text-gray-900">
                    {selectedFirmVisit.employee_name || '-'}
                    {selectedFirmVisit.employee_code && <span className="ml-1 text-gray-400 text-xs">({selectedFirmVisit.employee_code})</span>}
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <CalendarDays className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Date</p>
                  <p className="text-sm text-gray-900">{selectedFirmVisit.tour_plan_date ? fmtDate(selectedFirmVisit.tour_plan_date) : '-'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Time</p>
                  <p className="text-sm text-gray-900">
                    {fmtTime(selectedFirmVisit.from)} – {selectedFirmVisit.to ? fmtTime(selectedFirmVisit.to) : 'Ongoing'}
                  </p>
                </div>
              </div>
              {selectedFirmVisit.route_name && (
                <div className="flex items-start gap-3">
                  <Navigation className="w-5 h-5 text-teal-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Route</p>
                    <p className="text-sm text-gray-900">{selectedFirmVisit.route_name}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-2 pt-1">
                <span className="text-xs text-gray-500 font-medium uppercase tracking-wide">Status</span>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(selectedFirmVisit.status, selectedFirmVisit.skip)}`}>
                  {selectedFirmVisit.skip ? "Skipped" : selectedFirmVisit.status?.charAt(0).toUpperCase() + selectedFirmVisit.status?.slice(1)}
                </span>
              </div>
              {selectedFirmVisit.details && (
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide mb-1">Details</p>
                  <p className="text-sm text-gray-700">{selectedFirmVisit.details}</p>
                </div>
              )}
            </div>
            <div className="px-5 py-4 border-t border-gray-100 flex justify-end">
              <button
                onClick={() => setSelectedFirmVisit(null)}
                className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default VisitPlanningPage;
